import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useContext, useEffect, useState } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { MODALS_ID, PAGE_NAME, QUERY_KEY } from '../../constants'

import { toast } from '../../utils'
import { abrCompleteDepositAccountRegister, abrStartDepositAccountRegister } from '../../utils/abr'

import { createDepositAccount } from '../../api/depositAccount'
import { getTermsList } from '../../api/terms'

import { StoresContext } from '../../provider/StoreProvider'
import { ModalContext } from '../../provider/ModalProvider'

import { useNavigate } from '../../hooks/useNavigate'
import { useUserInfo } from '../../hooks/useUserInfo'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { ColumnItem } from '../../components/common'
import { Button } from '../../components/button'
import { Text } from '../../components/text'
import { TermsBox } from '../../components/termsBox'
import { Footer } from '../../components/footer'
import { Header } from '../../components/header'
import { TermsDetailModal } from '../../components/modals/TermsDetailModal'

export function DepositTerms() {
  const {
    commonStore: { setShowTab, setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [terms, setTerms] = useState<{ termsId: number; approved: boolean; required: boolean }[]>([])
  const [termsId, setTermsId] = useState<number | undefined>()

  const queryClient = useQueryClient()
  const { data: userInfo, isLogin, isCheckingLogin } = useUserInfo()
  const navigate = useNavigate()

  const {
    data: termsQuery,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QUERY_KEY.termsList, { page: PAGE_NAME.DEPOSIT_ACCOUNT }],
    queryFn: async () => await getTermsList(PAGE_NAME.DEPOSIT_ACCOUNT),
  })
  const { mutate } = useMutation({
    mutationFn: createDepositAccount,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.depositAccount] })
      abrCompleteDepositAccountRegister()
      ChannelService.track('complete_deposit_account_Register')
      navigate('/account/deposit/success', { replace: true, state: { prevPathname: window.location.pathname } })
    },
    onError: (error) => {
      const defaultErrorMessage = '예치금 계좌 생성에 실패했어요.'
      if (isAxiosError(error)) {
        toast(error.response?.data.message || defaultErrorMessage)
        return
      }
      toast(defaultErrorMessage)
    },
  })

  const isDepositAccount = !!userInfo?.depositAccount

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isDepositAccount) {
      setInvalidAccess(true)
    }
  }, [userInfo])

  useEffect(() => {
    setShowTab(false)
    abrStartDepositAccountRegister()
    ChannelService.track('start_deposit_account_register')
  }, [])

  useEffect(() => {
    if (typeof termsId === 'number') openTermsDetailModal()
  }, [termsId])

  useEffect(() => {
    if (!termsQuery) return
    setTerms(termsQuery.map(({ id, required }) => ({ required, termsId: id, approved: false })))
  }, [termsQuery])

  const openTermsDetailModal = () => {
    if (typeof termsId !== 'number') return

    openModal({
      id: MODALS_ID.TERMS_DETAIL,
      Component: () => (
        <TermsDetailModal
          termsId={termsId}
          isScrollEvent={true}
          buttonProps={{ type: 'primary' }}
          onDismiss={(result) => {
            setTermsId(undefined)
            closeModal(MODALS_ID.TERMS_DETAIL)
            if (!result) return false

            setTerms((prevState) => prevState.map((v) => (v.termsId === termsId ? { ...v, approved: true } : v)))
          }}
        />
      ),
    })
  }

  if (isDepositAccount || isPending || isError) {
    return null
  }

  const create = async () => {
    mutate({ terms: terms.map(({ termsId, approved }) => ({ termsId, approved })) })
  }

  return (
    <Wrap>
      <Content>
        <Header type="SUB" title="예치금 계좌 생성하기" />
        <Container>
          <Text size={24} weight={600} lineHeight={34}>
            아래의 약관에 동의 시
            <br />
            예치금 충전 계좌가 발급돼요.
          </Text>
          <Terms $gap={16}>
            {termsQuery.map(({ id, name, required }) => {
              const isAgreed = terms.find(({ termsId }) => termsId === id)?.approved ?? false
              return (
                <TermsBox
                  key={id}
                  title={`(${required ? '필수' : '선택'}) ${name}`}
                  isAgreed={isAgreed}
                  onChange={(checked) =>
                    checked
                      ? setTermsId(id)
                      : setTerms((prevState) =>
                          prevState.map((v) => (v.termsId === id ? { ...v, approved: false } : v)),
                        )
                  }
                />
              )
            })}
          </Terms>
        </Container>
      </Content>
      <Footer>
        <Button
          type="primary"
          size="large"
          disabled={!terms.every(({ approved, required }) => (required ? approved : true))}
          block={true}
          onClick={create}
        >
          다음
        </Button>
      </Footer>
    </Wrap>
  )
}

const Container = styled.div`
  padding: 56px 24px 24px;
`

const Terms = styled(ColumnItem)`
  margin-top: 32px;
`
